import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '@/lib/utils';
import { saveAuthSession } from '@/lib/auth-session';
import { resetLoginDocumentBranding } from '@/lib/tenant-brand';
import AuthPageShell from '@/components/AuthPageShell';
import OtpCodeInput from '@/components/OtpCodeInput';

export default function AcceptTeamInvitePage() {
  const [email, setEmail] = useState('');
  const [preview, setPreview] = useState(null);
  const [validating, setValidating] = useState(true);
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    resetLoginDocumentBranding('Accept invitation · AivoryEMS');
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = String(params.get('email') || '').trim().toLowerCase();
    setEmail(fromUrl);
    if (!fromUrl) {
      setValidating(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/auth/team-invite/preview?email=${encodeURIComponent(fromUrl)}`,
        );
        const body = await res.json().catch(() => ({}));
        if (!cancelled) {
          if (res.ok) {
            setPreview(body);
            setName(String(body.name || ''));
          } else {
            setPreview(null);
          }
        }
      } catch {
        if (!cancelled) setPreview(null);
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const resendCode = async () => {
    if (!email) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/team-invite/request-otp`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || 'Could not resend code');
      toast.success(body.emailSent ? 'Verification code sent' : 'Code created', {
        description: body.emailSent ? `Check ${email}` : 'Email delivery failed',
      });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const accept = async (e) => {
    e.preventDefault();
    if (!email || otp.length !== 6 || password.length < 8) {
      toast.error('Enter the 6-digit code and a password of at least 8 characters');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/team-invite/accept`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email, code: otp, password, name }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || 'Could not accept invitation');
      saveAuthSession({
        email: body.email,
        token: body.token,
        via: 'team_invite',
        role: body.role || 'admin',
        tenant: body.tenant,
      });
      setDone(true);
      toast.success(`Welcome to ${body.workspaceName || 'your workspace'}`);
      window.setTimeout(() => {
        window.location.assign('/admin');
      }, 800);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthPageShell variant="centered" documentTitle="Accept invitation · AivoryEMS">
      <div className="text-overline">Team invitation</div>
      <h1 className="font-heading text-2xl mt-2">Join your workspace</h1>

      {validating ? (
        <p className="mt-6 text-sm text-[var(--text-secondary)]">Checking invitation…</p>
      ) : !preview ? (
        <div className="mt-6 space-y-3 text-sm text-[var(--text-secondary)]">
          <p>No pending invitation was found for this email.</p>
          <input
            className="input-dark w-full"
            placeholder="Work email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value.trim().toLowerCase())}
          />
          <button type="button" className="btn-ghost text-sm cursor-pointer" onClick={resendCode} disabled={busy || !email}>
            Resend verification code
          </button>
          <p>
            <Link to="/login" className="text-[var(--primary)]">
              Back to login
            </Link>
          </p>
        </div>
      ) : done ? (
        <p className="mt-6 text-sm text-[var(--text-secondary)]">Redirecting to your workspace…</p>
      ) : (
        <form onSubmit={accept} className="mt-6 space-y-4">
          <p className="text-sm text-[var(--text-secondary)]">
            You were invited to <strong>{preview.workspaceName}</strong> as <strong>{preview.role}</strong>.
            Enter the 6-digit code from your email and choose a password.
          </p>
          <input className="input-dark w-full" value={email} readOnly />
          <input
            className="input-dark w-full"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <OtpCodeInput value={otp} onChange={setOtp} disabled={busy} />
          <input
            className="input-dark w-full"
            placeholder="Password (min 8 chars)"
            type="password"
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          <button type="submit" className="btn-primary w-full justify-center min-h-[44px] cursor-pointer" disabled={busy}>
            {busy ? 'Joining…' : 'Verify & join workspace'} <ArrowRight size={14} />
          </button>
          <button type="button" className="text-xs text-[var(--text-tertiary)] hover:text-[var(--text-primary)] cursor-pointer" onClick={resendCode} disabled={busy}>
            Resend verification code
          </button>
        </form>
      )}
    </AuthPageShell>
  );
}
